import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import {LinkBasic} from 'UI/components/action/LinkBasic';

import {useFooterLink} from './useFooterLink';

import type {LinkField} from 'types/payload-types';

/**
 * The props for the `FooterLink` component, which renders a link in the footer section.
 * This interface defines the structure of the expected props, including the link configuration
 * and an optional test identifier for end-to-end testing. The `link` prop determines how the link
 * behaves (internal, external, or download), while the `testId` is applied as a `data-cy` attribute
 * to facilitate reliable test targeting.
 */
interface ComponentProps {
    /**
     * The navigation link configuration containing metadata such as label, type,
     * and destination. This determines how the footer link is rendered and behaves.
     */
    link: LinkField;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FooterLink'
     */
    testId?: string;
}

/**
 * A footer link component that renders a styled anchor element with proper attributes.
 * This component uses the `useFooterLink` hook to resolve link properties such as `href`, `rel`,
 * `target`, `download`, and `label`. It ensures consistent link behavior in the footer section,
 * supporting internal navigation, external links, file downloads, and accessibility requirements.
 * The `testId` prop is applied as a `data-cy` attribute, enabling reliable targeting in
 * end-to-end testing frameworks like Cypress.
 *
 * @param props        The component props.
 * @param props.link   The link object containing metadata for rendering the footer link.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'FooterLink'`.
 * @returns A JSX element rendering the footer link with the correct attributes.
 *
 * @example
 * ```tsx
 * <FooterLink link={{ label: 'Privacy Policy', linkType: 'internal', internal: '/privacy' }} />
 * ```
 */
const FooterLink = observer(({link, testId = 'FooterLink'}: ComponentProps) => {
    const {href, isDownload, label, rel, target} = useFooterLink(link);

    return (
        <Wrapper data-cy={testId} download={isDownload} href={href!} rel={rel} target={target}>{label}</Wrapper>
    );
});

FooterLink.displayName = 'FooterLink';

export {FooterLink};

const Wrapper = styled(LinkBasic)`
    color: ${({theme}) => theme.colors.textSecondaryInverted};
    font-family: ${({theme}) => theme.fonts.Poppins};
    font-size: 1.2rem;
    line-height: 1.5;
    margin: 0;
    padding: 0;
    text-decoration: underline hsla(from ${({theme}) => theme.colors.textSecondaryInverted} H S L / 0);
    transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out, color 0.2s ease-in-out;

    &:hover {
        color: ${({theme}) => theme.colors.textPrimaryInverted};
        text-decoration: underline ${({theme}) => theme.colors.textPrimaryInverted};
        transition: outline 0.2s ease-in-out, text-decoration 0.2s ease-in-out, color 0.2s ease-in-out;
    }
`;